import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import {
  BoxSelect,
  Camera,
  Crosshair,
  Download,
  Layers3,
  LocateFixed,
  MapPin,
  Maximize2,
  MousePointer2,
  PenLine,
  RotateCw,
  Ruler,
  Share2,
  X,
} from 'lucide-react';
import { countryLayer, nightLightsLayer } from '../map/data/demoLayers';
import type {
  CameraState,
  DrawResult,
  EngineId,
  InteractionMode,
  LayerManifest,
  LayerStyle,
  Selection,
  SpatialFeature,
  TimeContext,
} from '../map/core/types';
import { formatArea, formatDistance } from '../map/core/geometry';
import { getLayerFeatures } from '../map/core/features';
import { WorldViewport, type WorldViewportHandle } from '../map/WorldViewport';

type MarkerTone = 'blue' | 'cyan' | 'green' | 'yellow' | 'orange' | 'red' | 'purple';

export type MapMarker = {
  id: string;
  longitude: number;
  latitude: number;
  altitude?: number;
  tone?: MarkerTone;
  label?: string;
  pulse?: boolean;
  glyph?: string;
  status?: string;
  metadata?: Record<string, unknown>;
};

const toneColor: Record<MarkerTone, string> = {
  blue: '#2f80ff', cyan: '#28d7f2', green: '#3ddc84', yellow: '#f5c542',
  orange: '#ff8a34', red: '#ff4d4f', purple: '#a86bf2',
};

export function MapViewport({
  markers = [],
  layers = [],
  initialCamera,
  engine,
  className = '',
  toolbar = true,
  zoom = true,
  compass = true,
  showCountries = true,
  showNightLights = true,
  children,
  onMarkerClick,
  onSelection,
  controllerRef,
  time,
}: {
  markers?: MapMarker[];
  layers?: LayerManifest[];
  initialCamera?: CameraState;
  engine?: EngineId;
  className?: string;
  toolbar?: boolean;
  zoom?: boolean;
  compass?: boolean;
  showCountries?: boolean;
  showNightLights?: boolean;
  children?: ReactNode;
  onMarkerClick?: (marker: MapMarker) => void;
  onSelection?: (selection: Selection) => void;
  controllerRef?: MutableRefObject<WorldViewportHandle | null>;
  time?: TimeContext;
}) {
  const viewportRef = useRef<WorldViewportHandle | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [activeTool, setActiveTool] = useState<InteractionMode>('select');
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const [isMaximized, setMaximized] = useState(false);
  const [sharedLayerFilter] = useState<Set<string> | undefined>(() => readLayerFilterFromUrl());
  const [layerOrder, setLayerOrder] = useState<string[]>(() => readLayerOrderFromUrl() ?? readStoredLayerOrder() ?? []);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [opacity, setOpacity] = useState<Record<string, number>>({});
  const [styleOverrides, setStyleOverrides] = useState<Record<string, LayerStyle>>({});
  const [measurement, setMeasurement] = useState<DrawResult>();
  const [selected, setSelected] = useState<Selection>({ type: 'none' });
  const [requestedEngine, setRequestedEngine] = useState<EngineId | undefined>(() => engine ?? readEngineFromUrl() ?? readPreferredEngine());
  const [resolvedEngine, setResolvedEngine] = useState<EngineId>();
  const [autoRotate, setAutoRotate] = useState(false);
  const [fallbackMessage, setFallbackMessage] = useState<string>();
  const [layerErrors, setLayerErrors] = useState<Record<string, string>>({});
  const [featureQuery, setFeatureQuery] = useState('');

  useEffect(() => {
    if (!engine) return;
    setRequestedEngine(engine);
    setResolvedEngine(undefined);
  }, [engine]);

  useEffect(() => {
    const onFullscreenChange = () => setMaximized(document.fullscreenElement === hostRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const host = hostRef.current;
      if (!host || !(event.target instanceof Node) || !host.contains(event.target)) return;
      if (event.key === 'Escape') {
        setMeasurement(undefined);
        setLayerPanelOpen(false);
        setSelected({ type: 'none' });
        viewportRef.current?.clearTransient();
        viewportRef.current?.clearSelection();
      } else if (event.key.toLowerCase() === 'l' && !isEditableTarget(event.target)) {
        event.preventDefault();
        setLayerPanelOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const markerLayer = useMemo<LayerManifest>(() => ({
    id: 'viewport-markers',
    title: 'Viewport markers',
    kind: 'entities',
    visible: visibility['viewport-markers'] ?? (sharedLayerFilter ? sharedLayerFilter.has('viewport-markers') : true),
    opacity: opacity['viewport-markers'] ?? 1,
    interactive: true,
    data: markers.map(markerToFeature),
    style: { color: '#2f80ff', radius: 7, labelField: 'label', clampToGround: true, ...(styleOverrides['viewport-markers'] ?? {}) },
    metadata: { source: 'screen view model', stateType: 'observed', classification: 'public' },
  }), [markers, opacity, sharedLayerFilter, styleOverrides, visibility]);

  const baseLayers = useMemo(() => {
    const result: LayerManifest[] = [];
    if (showCountries) result.push({ ...countryLayer, style: { ...countryLayer.style, ...(styleOverrides[countryLayer.id] ?? {}) }, visible: visibility[countryLayer.id] ?? (sharedLayerFilter ? sharedLayerFilter.has(countryLayer.id) : countryLayer.visible), opacity: opacity[countryLayer.id] ?? countryLayer.opacity });
    if (showNightLights) result.push({ ...nightLightsLayer, style: { ...nightLightsLayer.style, ...(styleOverrides[nightLightsLayer.id] ?? {}) }, visible: visibility[nightLightsLayer.id] ?? (sharedLayerFilter ? sharedLayerFilter.has(nightLightsLayer.id) : nightLightsLayer.visible), opacity: opacity[nightLightsLayer.id] ?? nightLightsLayer.opacity });
    result.push(markerLayer);
    for (const layer of layers) result.push({ ...layer, style: { ...layer.style, ...(styleOverrides[layer.id] ?? {}) }, visible: visibility[layer.id] ?? (sharedLayerFilter ? sharedLayerFilter.has(layer.id) : layer.visible), opacity: opacity[layer.id] ?? layer.opacity });
    return result;
  }, [layers, markerLayer, opacity, sharedLayerFilter, showCountries, showNightLights, styleOverrides, visibility]);

  useEffect(() => {
    const ids = baseLayers.map((layer) => layer.id);
    setLayerOrder((current) => {
      const retained = current.filter((id) => ids.includes(id));
      const appended = ids.filter((id) => !retained.includes(id));
      const next = [...retained, ...appended];
      return next.join('|') === current.join('|') ? current : next;
    });
  }, [baseLayers]);

  useEffect(() => {
    try { window.localStorage.setItem('world-twin.layer-order', JSON.stringify(layerOrder)); } catch { /* storage may be disabled */ }
  }, [layerOrder]);

  const effectiveLayers = useMemo(() => {
    const rank = new Map(layerOrder.map((id, index) => [id, index]));
    return [...baseLayers].sort((a, b) => (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  }, [baseLayers, layerOrder]);

  const displayLayers = useMemo(() => [...effectiveLayers].reverse(), [effectiveLayers]);

  const accessibleFeatures = useMemo(() => {
    const query = featureQuery.trim().toLowerCase();
    return effectiveLayers
      .filter((layer) => layer.visible !== false && layer.interactive !== false)
      .flatMap((layer) => getLayerFeatures(layer).map((feature) => ({
        layer,
        feature,
        label: String(feature.properties?.label ?? feature.properties?.name ?? feature.id ?? 'Unnamed feature'),
      })))
      .filter(({ label, layer }) => !query || `${label} ${layer.title}`.toLowerCase().includes(query))
      .sort((a, b) => featureLayerPriority(a.layer) - featureLayerPriority(b.layer))
      .slice(0, 120);
  }, [effectiveLayers, featureQuery]);

  const setTool = (tool: InteractionMode) => {
    setActiveTool(tool);
    setMeasurement(undefined);
    viewportRef.current?.setTool(tool);
  };

  const moveLayer = (layerId: string, direction: -1 | 1) => {
    setLayerOrder((current) => {
      const index = current.indexOf(layerId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const setLayerColor = (layer: LayerManifest, color: string) => {
    const hasPolygon = getLayerFeatures(layer).some((feature) => feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon');
    setStyleOverrides((current) => ({
      ...current,
      [layer.id]: { ...current[layer.id], color, ...(hasPolygon ? { fillColor: color } : {}) },
    }));
  };

  const handleSelection = (selection: Selection) => {
    setSelected(selection);
    const marker = markers.find((candidate) => String(candidate.id) === String(selection.featureId));
    if (marker) onMarkerClick?.(marker);
    onSelection?.(selection);
  };

  const clearSelection = () => {
    setSelected({ type: 'none' });
    viewportRef.current?.clearSelection();
  };

  const selectAccessibleFeature = (layer: LayerManifest, feature: SpatialFeature) => {
    if (!feature.geometry) return;
    const selection: Selection = {
      type: 'feature',
      layerId: layer.id,
      featureId: feature.id,
      feature,
    };
    viewportRef.current?.setSelection(selection);
    void viewportRef.current?.flyTo(feature.geometry, { durationMs: 750 });
  };

  const handleMeasure = (result: DrawResult) => setMeasurement(result);

  const capture = async () => {
    try {
      const blob = await viewportRef.current?.captureSnapshot();
      if (!blob) return;
      downloadBlob(blob, `world-twin-map-${new Date().toISOString().replace(/[:.]/g, '-')}.png`);
    } catch (error) {
      showTransientMessage(setFallbackMessage, toError(error).message);
    }
  };

  const exportGeoJson = () => {
    const collection = viewportRef.current?.exportVisibleFeatures();
    if (!collection) return;
    downloadBlob(new Blob([JSON.stringify(collection, null, 2)], { type: 'application/geo+json' }), 'world-twin-visible-layers.geojson');
  };

  const share = async () => {
    const renderer = viewportRef.current?.getRenderer();
    const camera = renderer?.getCamera();
    const url = new URL(window.location.href);
    if (camera) {
      url.searchParams.set('lon', camera.longitude.toFixed(5));
      url.searchParams.set('lat', camera.latitude.toFixed(5));
      url.searchParams.set('height', Math.round(camera.height).toString());
    }
    url.searchParams.set('engine', viewportRef.current?.getEngine() ?? resolvedEngine ?? requestedEngine ?? 'native');
    const enabledLayers = effectiveLayers.filter((layer) => layer.visible !== false).map((layer) => layer.id);
    url.searchParams.set('layers', enabledLayers.join(','));
    url.searchParams.set('layerOrder', effectiveLayers.map((layer) => layer.id).join(','));
    if (time?.currentTime) url.searchParams.set('time', time.currentTime);
    try {
      await copyText(url.toString());
      showTransientMessage(setFallbackMessage, 'Shareable map URL copied');
    } catch (error) {
      showTransientMessage(setFallbackMessage, toError(error).message);
    }
  };

  const toggleMaximize = async () => {
    if (!hostRef.current) return;
    try {
      if (!document.fullscreenElement && hostRef.current.requestFullscreen) {
        await hostRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else setMaximized((current) => !current);
    } catch (error) {
      showTransientMessage(setFallbackMessage, toError(error).message);
    }
  };

  return (
    <div ref={hostRef} className={`map-viewport map-viewport--real ${isMaximized ? 'is-maximized' : ''} ${className}`}>
      <WorldViewport
        ref={(handle) => { viewportRef.current = handle; if (controllerRef) controllerRef.current = handle; }}
        layers={effectiveLayers}
        initialCamera={readCameraFromUrl() ?? initialCamera ?? inferCamera(markers)}
        engine={requestedEngine}
        time={time}
        onSelection={handleSelection}
        onMeasureUpdated={handleMeasure}
        onDrawCompleted={(result) => {
          if (result.distanceMeters || result.areaSquareMeters) setMeasurement(result);
          else {
            const blob = new Blob([JSON.stringify({ type: 'Feature', properties: { createdBy: 'World Twin' }, geometry: result.geometry }, null, 2)], { type: 'application/geo+json' });
            downloadBlob(blob, `world-twin-${result.mode}.geojson`);
          }
        }}
        onReady={(rendererEngine) => {
          setResolvedEngine(rendererEngine);
          setFallbackMessage(undefined);
        }}
        onEngineFallback={(from, to, error) => setFallbackMessage(`${from} unavailable: ${error.message}. Running ${to}.`)}
        onLayerLoaded={(layerId) => setLayerErrors((current) => {
          if (!(layerId in current)) return current;
          const next = { ...current };
          delete next[layerId];
          return next;
        })}
        onLayerError={(layerId, error) => setLayerErrors((current) => ({ ...current, [layerId]: error.message }))}
        onError={(error) => setFallbackMessage(error.message)}
      >
        {children}
      </WorldViewport>

      {toolbar && (
        <div className="map-toolbar" aria-label="Map tools">
          <ToolButton title="Select and navigate" active={activeTool === 'select'} onClick={() => setTool('select')}><MousePointer2 size={17} /></ToolButton>
          <ToolButton title="Inspect features" active={activeTool === 'inspect'} onClick={() => setTool('inspect')}><Crosshair size={17} /></ToolButton>
          <ToolButton title="Draw point" active={activeTool === 'draw-point'} onClick={() => setTool('draw-point')}><MapPin size={17} /></ToolButton>
          <ToolButton title="Draw line" active={activeTool === 'draw-line'} onClick={() => setTool('draw-line')}><PenLine size={17} /></ToolButton>
          <ToolButton title="Draw polygon" active={activeTool === 'draw-polygon'} onClick={() => setTool('draw-polygon')}><BoxSelect size={17} /></ToolButton>
          <ToolButton title="Manage layers" active={layerPanelOpen} onClick={() => setLayerPanelOpen((open) => !open)}><Layers3 size={17} /></ToolButton>
          <ToolButton title="Measure distance" active={activeTool === 'measure-distance'} onClick={() => setTool('measure-distance')}><Ruler size={17} /></ToolButton>
          <ToolButton title="Measure area" active={activeTool === 'measure-area'} onClick={() => setTool('measure-area')}><BoxSelect size={17} /></ToolButton>
          <ToolButton title={autoRotate ? 'Stop auto rotate' : 'Start auto rotate'} active={autoRotate} onClick={() => { const next = !autoRotate; setAutoRotate(next); viewportRef.current?.setAutoRotate(next); }}><RotateCw size={17} /></ToolButton>
          <ToolButton title="Share current camera" onClick={() => void share()}><Share2 size={17} /></ToolButton>
          <ToolButton title="Capture PNG" onClick={() => void capture()}><Camera size={17} /></ToolButton>
        </div>
      )}

      <button type="button" className="map-maximize" aria-label="Maximize viewport" onClick={() => void toggleMaximize()}><Maximize2 size={16} /></button>

      {zoom && (
        <div className="map-zoom-controls" aria-label="Map zoom controls">
          <button type="button" aria-label="Zoom in" onClick={() => viewportRef.current?.zoomBy(1.35)}>＋</button>
          <button type="button" aria-label="Zoom out" onClick={() => viewportRef.current?.zoomBy(0.74)}>−</button>
          <button type="button" aria-label="Export visible layers" title="Export GeoJSON" onClick={exportGeoJson}><Download size={15} /></button>
        </div>
      )}

      {compass && (
        <button type="button" className="compass" aria-label="Reset map camera" onClick={() => viewportRef.current?.resetCamera()}>
          <span>N</span>
          <LocateFixed size={18} />
        </button>
      )}

      {layerPanelOpen && (
        <div className="map-layer-manager">
          <header><strong>Spatial layers</strong><button type="button" onClick={() => setLayerPanelOpen(false)} aria-label="Close layer manager"><X size={15} /></button></header>
          <div className="map-layer-manager__layers">
            {displayLayers.map((layer, index) => (
              <div key={layer.id} className="map-layer-manager__layer">
                <label className="map-layer-manager__identity">
                  <input
                    type="checkbox"
                    checked={layer.visible !== false}
                    onChange={(event) => setVisibility((current) => ({ ...current, [layer.id]: event.target.checked }))}
                  />
                  <span>
                    <strong>{layer.title}</strong>
                    <small>{layer.metadata?.source ?? layer.kind} · {layer.metadata?.stateType ?? 'unknown'} · {layer.metadata?.classification ?? 'unclassified'}</small>
                  </span>
                </label>
                <div className="map-layer-manager__order" aria-label={`${layer.title} layer order`}>
                  <button type="button" disabled={index === 0} onClick={() => moveLayer(layer.id, 1)} aria-label={`Move ${layer.title} up`}>↑</button>
                  <button type="button" disabled={index === displayLayers.length - 1} onClick={() => moveLayer(layer.id, -1)} aria-label={`Move ${layer.title} down`}>↓</button>
                </div>
                <div className="map-layer-manager__style">
                  <label><span>Color</span><input type="color" value={resolveLayerColor(layer)} onChange={(event) => setLayerColor(layer, event.target.value)} aria-label={`${layer.title} color`} /></label>
                </div>
                <div className="map-layer-manager__opacity">
                  <small>Opacity {Math.round((layer.opacity ?? 1) * 100)}%</small>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={layer.opacity ?? 1}
                    onChange={(event) => setOpacity((current) => ({ ...current, [layer.id]: Number(event.target.value) }))}
                    aria-label={`${layer.title} opacity`}
                  />
                </div>
                {layerErrors[layer.id] && (
                  <div className="map-layer-manager__error" role="alert">
                    <span>{layerErrors[layer.id]}</span>
                    <button type="button" onClick={() => {
                      void viewportRef.current?.retryLayer(layer.id)
                        .then(() => setLayerErrors((current) => {
                          const next = { ...current };
                          delete next[layer.id];
                          return next;
                        }))
                        .catch((error) => setLayerErrors((current) => ({ ...current, [layer.id]: toError(error).message })));
                    }}><RotateCw size={12} /> Retry</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <section className="map-feature-browser" aria-label="Map feature list">
            <header><strong>Keyboard feature list</strong><small>{accessibleFeatures.length} shown</small></header>
            <input value={featureQuery} onChange={(event) => setFeatureQuery(event.target.value)} placeholder="Search visible features…" />
            <div>
              {accessibleFeatures.map(({ layer, feature, label }, index) => (
                <button type="button" key={`${layer.id}-${String(feature.id ?? index)}`} onClick={() => selectAccessibleFeature(layer, feature)}>
                  <span>{label}</span><small>{layer.title}</small>
                </button>
              ))}
              {!accessibleFeatures.length && <p>No visible features match this search.</p>}
            </div>
          </section>
          <footer>
            <span>Engine</span>
            <select value={requestedEngine ?? resolvedEngine ?? 'cesium'} onChange={(event) => {
              const next = event.target.value as EngineId;
              setRequestedEngine(next);
              setResolvedEngine(undefined);
            }}>
              <option value="cesium">CesiumJS</option>
              <option value="maplibre">MapLibre GL</option>
              <option value="native">Native fallback</option>
            </select>
          </footer>
        </div>
      )}

      {measurement && (
        <div className="map-measurement" role="status">
          <Ruler size={15} />
          <span>{measurement.distanceMeters !== undefined ? formatDistance(measurement.distanceMeters) : formatArea(measurement.areaSquareMeters ?? 0)}</span>
          <button type="button" onClick={() => { setMeasurement(undefined); viewportRef.current?.clearTransient(); }} aria-label="Clear measurement"><X size={14} /></button>
        </div>
      )}

      {selected.type === 'feature' && selected.feature && (
        <div className="map-selection-chip">
          <span>{String(selected.feature.properties?.label ?? selected.feature.properties?.name ?? selected.featureId)}</span>
          <small>{selected.layerId}</small>
          <button type="button" aria-label="Clear map selection" onClick={clearSelection}><X size={13} /></button>
        </div>
      )}

      {fallbackMessage && <div className="map-engine-message" role="status">{fallbackMessage}</div>}
    </div>
  );
}

function ToolButton({ title, active, onClick, children }: { title: string; active?: boolean; onClick: () => void; children: ReactNode }) {
  return <button type="button" title={title} aria-pressed={active} className={active ? 'is-active' : ''} onClick={onClick}>{children}</button>;
}

function markerToFeature(marker: MapMarker): SpatialFeature {
  const { longitude, latitude } = marker;
  const tone = marker.tone ?? 'blue';
  return {
    type: 'Feature',
    id: marker.id,
    properties: {
      label: marker.label ?? marker.id,
      name: marker.label ?? marker.id,
      color: toneColor[tone],
      tone,
      glyph: marker.glyph,
      pulse: marker.pulse,
      status: marker.status,
      ...(marker.metadata ?? {}),
    },
    geometry: { type: 'Point', coordinates: [longitude, latitude, marker.altitude ?? 0] },
  };
}

function inferCamera(markers: MapMarker[]): CameraState {
  if (!markers.length) return { longitude: 5, latitude: 15, height: 18_500_000, pitch: -90 };
  const positions = markers.map((marker) => [marker.longitude, marker.latitude]);
  const longitude = positions.reduce((sum, position) => sum + position[0], 0) / positions.length;
  const latitude = positions.reduce((sum, position) => sum + position[1], 0) / positions.length;
  return { longitude, latitude, height: markers.length > 8 ? 19_000_000 : 14_000_000, pitch: -90 };
}


function resolveLayerColor(layer: LayerManifest): string {
  const value = layer.style?.fillColor ?? layer.style?.color ?? '#2f80ff';
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#2f80ff';
}

function featureLayerPriority(layer: LayerManifest): number {
  if (layer.id === 'viewport-markers') return 0;
  if (layer.id === 'countries') return 2;
  return 1;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable);
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Could not copy the map URL.');
}

function showTransientMessage(setMessage: (message?: string) => void, message: string): void {
  setMessage(message);
  window.setTimeout(() => setMessage(undefined), 2_600);
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
function readCameraFromUrl(): CameraState | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const longitude = Number(params.get('lon'));
  const latitude = Number(params.get('lat'));
  const height = Number(params.get('height'));
  if (![longitude, latitude, height].every(Number.isFinite)) return undefined;
  return { longitude, latitude, height, pitch: -90, heading: 0, roll: 0 };
}

function readEngineFromUrl(): EngineId | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = new URLSearchParams(window.location.search).get('engine');
  return value === 'cesium' || value === 'maplibre' || value === 'native' ? value : undefined;
}

function readLayerFilterFromUrl(): Set<string> | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = new URLSearchParams(window.location.search).get('layers');
  return raw ? new Set(raw.split(',').filter(Boolean)) : undefined;
}


function readPreferredEngine(): EngineId | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const preferences = JSON.parse(window.localStorage.getItem('world-twin.preferences') ?? '{}') as { defaultEngine?: unknown };
    return preferences.defaultEngine === 'cesium' || preferences.defaultEngine === 'maplibre' || preferences.defaultEngine === 'native'
      ? preferences.defaultEngine
      : undefined;
  } catch {
    return undefined;
  }
}

function readStoredLayerOrder(): string[] | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = JSON.parse(window.localStorage.getItem('world-twin.layer-order') ?? 'null') as unknown;
    return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : undefined;
  } catch {
    return undefined;
  }
}

function readLayerOrderFromUrl(): string[] | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = new URLSearchParams(window.location.search).get('layerOrder');
  return raw ? raw.split(',').filter(Boolean) : undefined;
}
